import React, { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const TIER_META = {
  Diamond:  { color: "#67e8f9", icon: "◆" },
  Platinum: { color: "#e2e8f0", icon: "◈" },
  Gold:     { color: "#fbbf24", icon: "◉" },
  Silver:   { color: "#94a3b8", icon: "◎" },
  Bronze:   { color: "#b45309", icon: "○" },
};

function Rank({ position }) {
  if (position === 1) return <span className="lbMedal lbGold">🥇</span>;
  if (position === 2) return <span className="lbMedal lbSilver">🥈</span>;
  if (position === 3) return <span className="lbMedal lbBronze">🥉</span>;
  return <span className="lbPosition">#{position}</span>;
}

function Avatar({ avatarUrl, displayName, size = 36 }) {
  const initials = (displayName || "?")[0].toUpperCase();
  return avatarUrl ? (
    <img
      src={avatarUrl}
      alt={displayName}
      className="lbAvatar"
      style={{ width: size, height: size }}
      referrerPolicy="no-referrer"
    />
  ) : (
    <div className="lbAvatarFallback" style={{ width: size, height: size }}>
      {initials}
    </div>
  );
}

export default function LeaderboardModal({ onClose }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    fetch(`${API_URL}/api/leaderboard`)
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load leaderboard.");
        return r.json();
      })
      .then((data) => setRows(data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="modalOverlay" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <section className="roomModal lbModal" role="dialog" aria-modal="true">
        <button className="modalClose" onClick={onClose} aria-label="Close leaderboard">x</button>

        <div className="modalHeader">
          <span className="modalEyebrow">Rankings</span>
          <h2>Leaderboard</h2>
          <p>Top players ranked by points earned across all games.</p>
        </div>

        {loading && <p className="lbStatus">Loading...</p>}
        {error   && <p className="lbStatus lbError">{error}</p>}

        {!loading && !error && rows.length === 0 && (
          <p className="lbStatus">No players yet. Play a game to appear here!</p>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className="lbTableWrap">
            <table className="lbTable">
              <thead>
                <tr>
                  <th>#</th>
                  <th colSpan={2}>Player</th>
                  <th>Points</th>
                  <th>Win%</th>
                  <th>Games</th>
                  <th>Streak</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const tier = TIER_META[row.rank_tier] || TIER_META.Bronze;
                  const winPct = row.games_played > 0
                    ? Math.round(row.win_rate * 100)
                    : 0;
                  return (
                    <tr key={row.user_id} className={i < 3 ? "lbTopRow" : ""}>
                      <td><Rank position={i + 1} /></td>
                      <td className="lbAvatarCell">
                        <Avatar avatarUrl={row.avatar_url} displayName={row.display_name} />
                      </td>
                      <td className="lbNameCell">
                        <span className="lbName">{row.display_name || "Player"}</span>
                        <span className="lbTierBadge" style={{ color: tier.color }}>
                          {tier.icon} {row.rank_tier || "Bronze"}
                        </span>
                      </td>
                      <td className="lbPoints">{row.rank_points ?? 1000}</td>
                      <td className="lbWinRate">{winPct}%</td>
                      <td className="lbGames">{row.games_played ?? 0}</td>
                      <td className="lbStreak">
                        {row.current_streak > 0
                          ? `🔥 ${row.current_streak}`
                          : row.best_streak > 0
                            ? `${row.best_streak} best`
                            : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
